import * as React from 'react'
import { StyledSlickComponent } from '../../../shared/styled-slick.component'
import { onlyUniqueByTransform } from '../../../../services/utils/lodash.service'
import styled, { keyframes } from 'styled-components'
import Slider from 'react-slick'
import { formatDurationFromHours, utilRange } from '../../../../services/utils/utils.service'

export const bounceDate = keyframes`
  25% { transform: translateY(-50%) translateX(-50%) scale(0); }
  75% { transform: translateY(-50%) translateX(-50%) scale(1.05); }
  100% { transform: translateY(-50%) translateX(-50%) scale(1); }
`

const minutesAppear = keyframes`
  0% { width: 0px; height: 0px; opacity: 0; }
  50% { width: 0px; height: 0px; opacity: 0; }
  99% { width: fit-content; height: 2.8em; opacity: 1; }
  100% { width: fit-content; height: auto; opacity: 1; }
`

const SliderContainer = styled.div`
  * { outline: none; }

  padding: 0 1.5em;
  background-color: ${ props => props.theme.colors.clr_0_lightest };
  border-radius: 0.25em;
  font-size: 1.1em;
`

const SliderItem = styled.div<{ $selected: boolean }>`
  position: relative;
  text-align: center;
  padding: 1em 0;
  cursor: pointer;
  ${ props => props.$selected && 'color: white; font-weight: bold;' }

  &:before {
    content: '';
    position: absolute;
    z-index: -1;
    top: 50%;
    left: 50%;
    display: block;
    width: 2em;
    height: 2em;
    border-radius: 50%;
    background: ${ props => props.theme.colors[props.$selected ? 'clr_1' : 'clr_0_light'] };
    transform: translateY(-50%) translateX(-50%) scale(${ props => props.$selected ? 1 : 0 });
  }

  &:hover:before {
    animation: ${bounceDate} .5s ease-in-out forwards;
  }
`

const MinutesContainer = styled.div`
  background-color: ${ props => props.theme.colors.clr_1 };
  margin-top: -1em;
  border-radius: 0.25em;
  padding: 0.4em 0.8em;
  margin-left: 3.3em;
  color: white;
  overflow: hidden;
  animation: ${minutesAppear} .5s ease-in-out forwards;
`

const Minute = styled.div<{ $selected: boolean }>`
  position: relative;
  display: inline-block;
  text-align: center;
  font-weight: ${ props => props.$selected ? 'bold' : 'normal '};
  margin: 0.8em;
  cursor: pointer;
`

interface Props {
  name: string
  values: number[]
  value: number
  onChange: (value: number) => void
}

export default function CastingTimePickerComponent ({ name, value, values, onChange }: Props) {
  const [, forceUpdate] = React.useReducer(x => x + 1, 0)
  const refSliderHours = React.useRef<Slider>()
  const refHourElements = React.useRef<{ [hour: number]: HTMLDivElement }>({})
  const choices = React.useMemo(() => values.filter(time => Math.trunc(time) === Math.trunc(value)), [value, values])

  React.useEffect(() => {
    // force refresh of view and display minutes if mounted with already set time
    if (typeof value === 'number') {
      refSliderHours.current.slickGoTo(values.filter(onlyUniqueByTransform(Math.trunc)).indexOf(Math.trunc(value)))
      forceUpdate()
    }
  }, [value, values])

  const setTime = React.useCallback((time: number, index?: number) => {
    if (typeof index !== 'undefined') refSliderHours.current.slickGoTo(index)
    onChange(time)
  }, [onChange])

  return <React.Fragment>
    <SliderContainer id={ `js-test-time-picker-${name}` }>
      <StyledSlickComponent arrows infinite={ false } swipeToSlide slidesToShow={ 6 } slidesToScroll={ 5 } ref={ refSliderHours }>
        { values.filter(onlyUniqueByTransform(Math.trunc)).map((time, index) => <SliderItem
          key={ time }
          id={ `${name}-${time}` }
          ref={ hourElement => refHourElements.current[Math.trunc(time)] = hourElement }
          onClick={ () => setTime(time, index) }
          $selected={ Math.trunc(time) === Math.trunc(value) }
          children={ Math.trunc(time) + 'h' } /> ) }

        { utilRange(1, 5).map(key => <div key={ key } />) }
      </StyledSlickComponent>
    </SliderContainer>

    { typeof value === 'number' && refHourElements.current[Math.trunc(value)] && choices.length > 1 && <MinutesContainer key={ Math.trunc(value) }>
      { choices.map(time => <Minute
        key={ time }
        $selected={ time === value }
        children={ formatDurationFromHours(time, { truncSpaces: true, hideMinIfHours: true }) }
        onClick={ () => setTime(time) } />) }
    </MinutesContainer> }
  </React.Fragment>
}
